import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, ReferenceLine, ReferenceArea,
  ResponsiveContainer, Tooltip,
} from "recharts";
import { Download, ArrowLeft, CheckCircle2, AlertTriangle, Sparkles, Loader2, MessageCircleHeart } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";

const LOGO_URL = "/cgi-logo.png";

/* --- Custom SVG sub-components for the 2-D chart --- */

// Wrapping bold label centered inside a ReferenceArea quadrant
function QuadrantLabel({ viewBox, lines }) {
  if (!viewBox) return null;
  const { x, y, width, height } = viewBox;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const lineH = 16;
  const startY = cy - ((lines.length - 1) * lineH) / 2;
  return (
    <text
      textAnchor="middle"
      fontSize="13"
      fontWeight="700"
      fill="#1f2436"
      style={{ pointerEvents: "none", fontFamily: "Fraunces, Georgia, serif" }}
    >
      {lines.map((line, i) => (
        <tspan key={i} x={cx} y={startY + i * lineH}>{line}</tspan>
      ))}
    </text>
  );
}

// Centered axis label (x or y), placed in the middle of each axis
function AxisLabel({ viewBox, text, axis }) {
  if (!viewBox) return null;
  const { x, y, width, height } = viewBox;
  if (axis === "x") {
    return (
      <text
        x={x + width / 2}
        y={y + height + 40}
        textAnchor="middle"
        fontSize="13"
        fontWeight="600"
        fill="#334155"
      >
        {text}
      </text>
    );
  }
  // y axis: rotate -90 around the midpoint of the axis
  const cx = x - 48;
  const cy = y + height / 2;
  return (
    <text
      x={cx}
      y={cy}
      textAnchor="middle"
      fontSize="13"
      fontWeight="600"
      fill="#334155"
      transform={`rotate(-90 ${cx} ${cy})`}
    >
      {text}
    </text>
  );
}

// Bigger "You Are Here" marker for the Scatter point
function YouAreHerePoint({ cx, cy }) {
  if (cx == null || cy == null) return null;
  return (
    <g style={{ pointerEvents: "none" }}>
      <circle cx={cx} cy={cy} r={18} fill="#2a3654" stroke="#0f172a" strokeWidth={3} />
      <circle cx={cx} cy={cy} r={6} fill="#fdf7ee" />
      <text
        x={cx}
        y={cy - 28}
        textAnchor="middle"
        fontSize="13"
        fontWeight="700"
        fill="#0f172a"
        style={{ fontFamily: "Fraunces, Georgia, serif" }}
      >
        You Are Here
      </text>
    </g>
  );
}

// Stable, module-scope label render functions used by Recharts
const UpperRightLabel  = (p) => <QuadrantLabel viewBox={p.viewBox} lines={["Calm Self-Expression", "with Empathic Listening"]} />;
const UpperLeftLabel   = (p) => <QuadrantLabel viewBox={p.viewBox} lines={["Calm Self-Expression", "with Self-Focused Listening"]} />;
const LowerRightLabel  = (p) => <QuadrantLabel viewBox={p.viewBox} lines={["Judgmental Expression", "with Empathic Listening"]} />;
const LowerLeftLabel   = (p) => <QuadrantLabel viewBox={p.viewBox} lines={["Judgmental Expression", "with Self-Focused Listening"]} />;
const XAxisLabelRender = (p) => <AxisLabel {...p} text="Self-Focused Listening  ←→  Empathic Listening" axis="x" />;
const YAxisLabelRender = (p) => <AxisLabel {...p} text="Blaming / Criticism  ←→  Calm Needs-Based Expression" axis="y" />;

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const LEVEL_COLORS = {
  // Positive direction (D1, D2, overall)
  "Strong": "bg-green-100 text-green-800",
  "Solid": "bg-amber-100 text-amber-900",
  "Developing": "bg-amber-100 text-amber-800",
  "Limited": "bg-orange-100 text-orange-800",
  "Significant Challenge": "bg-red-100 text-red-800",
  // Negative direction (D3 — reactivity)
  "Very Low Reactivity": "bg-green-100 text-green-800",
  "Low Reactivity": "bg-amber-100 text-amber-900",
  "Moderate Reactivity": "bg-amber-100 text-amber-800",
  "High Reactivity": "bg-orange-100 text-orange-800",
  // Supplemental (D4 — conflict engagement)
  "High Conflict Engagement": "bg-green-100 text-green-800",
  "Moderate Conflict Engagement": "bg-amber-100 text-amber-800",
  "Low Conflict Engagement": "bg-orange-100 text-orange-800",
};

function ScoreBar({ percent, negative = false }) {
  // Negative direction (e.g. reactivity): high % = challenge -> show in amber/red
  const cls = negative ? "bg-amber-500" : "bg-primary";
  return (
    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${cls} transition-all duration-700`} style={{ width: `${percent}%` }} />
    </div>
  );
}

function engagementPosition(percent) {
  if (percent < 30) return "Strongly Avoiding";
  if (percent < 45) return "Slightly Avoiding";
  if (percent <= 55) return "Balanced";
  if (percent <= 70) return "Slightly Engaging";
  return "Strongly Engaging";
}

export default function Results() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/assessment/${id}`);
        setData(data);
      } catch {
        toast.error("Could not load this assessment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const downloadPdf = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      let position = 0;
      let heightLeft = imgHeight;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`CCMS-Report-${(data?.created_at || "").slice(0, 10)}.pdf`);
      toast.success("Report downloaded.");
    } catch (e) {
      toast.error("PDF generation failed.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-clinical">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-20 flex items-center gap-3 text-slate-500" data-testid="results-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading your report…
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-clinical">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-slate-600">Assessment not found.</div>
      </div>
    );
  }

  const { subscales, overall_percent, overall_level, quadrant, chart_point, chart_axes, strengths, challenges, recommendations, created_at } = data;
  const engagement = subscales?.engagement;
  const skillVsEngagement = engagement?.combined_interpretation;

  const chartData = [{ x: chart_point.x, y: chart_point.y, name: "You" }];
  const ax = chart_axes || {
    x: { min: 10, max: 50, midpoint: 25.5, low_label: "Self-Focused Listening", high_label: "Empathic Listening" },
    y: { min: 10, max: 50, midpoint: 25.5, low_label: "Blaming / Criticism",    high_label: "Calm Needs-Based Expression" },
  };

  return (
    <div className="min-h-screen bg-clinical" data-testid="results-page">
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Top actions */}
        <div className="flex items-center justify-between mb-8 no-print">
          <Button asChild variant="outline" className="gap-2">
            <Link to="/" data-testid="results-back-button">
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
          </Button>
          <Button onClick={downloadPdf} disabled={downloading} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-testid="download-pdf-button">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {downloading ? "Preparing…" : "Download PDF report"}
          </Button>
        </div>

        {/* Report (captured into PDF) */}
        <div ref={reportRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="results-report">
          {/* Header */}
          <div className="p-8 sm:p-12 border-b border-slate-100">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Common Ground Institute" className="h-16 w-auto object-contain rounded-md" crossOrigin="anonymous" />
              <p className="text-sm text-slate-600">Capacity for Conflict Management Scale</p>
            </div>
            <h1 className="mt-8 font-heading text-3xl sm:text-4xl font-semibold text-slate-900">
              Your Conflict Management Report
            </h1>
            <p className="mt-2 text-slate-500 text-sm">Completed {formatDate(created_at)}</p>

            {/* Headline scores */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-accent rounded-xl p-6">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">Overall capacity</p>
                <p className="mt-2 font-heading text-5xl font-semibold text-slate-900" data-testid="overall-score">
                  {overall_percent}<span className="text-2xl text-slate-500">/100</span>
                </p>
                <p className="mt-1 text-primary font-medium">{overall_level}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Conflict style</p>
                <p className="mt-2 font-heading text-2xl font-semibold text-slate-900" data-testid="quadrant-name">
                  {quadrant.name}
                </p>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{quadrant.summary}</p>
              </div>
            </div>
          </div>

          {/* Quadrant Chart — primary visual report */}
          <section className="p-8 sm:p-12 border-b border-slate-100" data-testid="quadrant-section">
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">Your Current Conflict Management Style</h2>

            <div className="bg-cream rounded-xl p-6 sm:p-10 relative">
              {/* Outer X-axis title (centered over the two upper boxes) */}
              <div className="ml-[200px] mr-4 mb-2 text-center text-[11px] uppercase tracking-[0.22em] font-bold text-slate-800">
                Style of Listening
              </div>

              {/* Per-column listening labels (aligned to chart columns) */}
              <div className="grid grid-cols-2 gap-0 ml-[200px] mr-4 mb-2">
                <div className="text-center text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-700">
                  Self-Focused Listening
                </div>
                <div className="text-center text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-700">
                  Empathic Listening
                </div>
              </div>

              <div className="flex items-stretch">
                {/* Y-dimension title: rotated parallel to the y axis, centered between the row labels */}
                <div className="w-[100px] flex items-center justify-center" style={{ height: 480 }}>
                  <div
                    className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-800 whitespace-nowrap"
                    style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
                  >
                    Blaming vs. Expressing Needs
                  </div>
                </div>

                {/* Per-row blame labels (aligned to chart rows) */}
                <div className="flex flex-col w-[100px] pr-3" style={{ height: 480 }}>
                  <div className="flex-1 flex items-center justify-end text-right text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-700 leading-tight">
                    Low<br />Blame /<br />Criticism
                  </div>
                  <div className="flex-1 flex items-center justify-end text-right text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-700 leading-tight">
                    High<br />Blame /<br />Criticism
                  </div>
                </div>

                {/* The chart */}
                <div className="flex-1" style={{ height: 480, minHeight: 480 }}>
                  <ResponsiveContainer>
                    <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                      <ReferenceArea x1={25.5} x2={50}   y1={25.5} y2={50}   fill="#e6efd9" fillOpacity={0.65}
                        label={UpperRightLabel} />
                      <ReferenceArea x1={1}    x2={25.5} y1={25.5} y2={50}   fill="#fce8cc" fillOpacity={0.65}
                        label={UpperLeftLabel} />
                      <ReferenceArea x1={25.5} x2={50}   y1={1}    y2={25.5} fill="#fce8cc" fillOpacity={0.65}
                        label={LowerRightLabel} />
                      <ReferenceArea x1={1}    x2={25.5} y1={1}    y2={25.5} fill="#f5d6c8" fillOpacity={0.7}
                        label={LowerLeftLabel} />

                      <XAxis
                        type="number" dataKey="x" domain={[1, 50]}
                        tick={false} axisLine={{ stroke: "#94a3b8" }} tickLine={false}
                      />
                      <YAxis
                        type="number" dataKey="y" domain={[1, 50]}
                        tick={false} axisLine={{ stroke: "#94a3b8" }} tickLine={false} width={1}
                      />
                      <ZAxis range={[600, 600]} />
                      <ReferenceLine x={25.5} stroke="#475569" strokeWidth={1.5} />
                      <ReferenceLine y={25.5} stroke="#475569" strokeWidth={1.5} />
                      <Tooltip cursor={{ strokeDasharray: "3 3" }} formatter={(v, n) => [`${v}`, n === "x" ? "Empathic Listening" : "Calm Expression"]} />
                      <Scatter data={chartData} shape={YouAreHerePoint} />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </section>

          {/* Subscale scores — primary three dimensions that map to the grid */}
          <section className="p-8 sm:p-12 border-b border-slate-100" data-testid="subscales-section">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">Report · Part 1</p>
              <h2 className="mt-1 font-heading text-2xl font-semibold text-slate-900">Subscale scores behind the grid</h2>
              <p className="mt-1 text-slate-500 text-sm">
                The three primary dimensions below are the inputs that placed you on the quadrant above.
              </p>
            </div>
            <div className="space-y-6">
              {Object.entries(subscales)
                .filter(([key]) => key !== "engagement")
                .map(([key, s]) => {
                const isNegative = s.direction === "negative";
                const directionHint = isNegative
                  ? "Higher score = greater reactivity"
                  : "Higher score = greater capacity";
                return (
                  <div key={key} className="border border-slate-100 rounded-xl p-5" data-testid={`subscale-${key}`}>
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <h3 className="font-heading text-lg font-semibold text-slate-900">{s.label}</h3>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${LEVEL_COLORS[s.level] || "bg-slate-100 text-slate-700"}`}>
                          {s.level}
                        </span>
                        <span className="font-heading text-xl font-semibold text-slate-900">
                          {s.raw}<span className="text-sm text-slate-500">/{s.max}</span>
                        </span>
                      </div>
                    </div>
                    <div className="mt-3"><ScoreBar percent={s.percent} negative={isNegative} /></div>
                    <div className="mt-1 flex justify-between text-[11px] text-slate-400">
                      <span>{s.min}</span>
                      <span>midpoint {s.midpoint}</span>
                      <span>{s.max}</span>
                    </div>
                    <p className="mt-3 text-slate-600 text-sm leading-relaxed">{s.interpretation}</p>
                    <p className="mt-2 text-xs text-slate-400">{directionHint} · midpoint {s.midpoint}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Conflict Engagement — supplemental, separate report */}
          {engagement && (
            <section className="p-8 sm:p-12 border-b border-slate-100 bg-accent/40" data-testid="engagement-section">
              <div className="mb-2">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">Report · Part 2 · Separate measure</p>
                <h2 className="mt-1 font-heading text-2xl font-semibold text-slate-900">Conflict Engagement</h2>
              </div>
              <p className="text-slate-500 text-sm mb-6">
                A short measure of how willing you are to engage difficult conversations rather than avoid them.
                This score is reported on its own and does not affect your primary results above.
              </p>

              <div className="border border-slate-100 rounded-xl p-6 bg-white" data-testid="subscale-engagement">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{engagement.label}</h3>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${LEVEL_COLORS[engagement.level] || "bg-slate-100 text-slate-700"}`}>
                      {engagementPosition(engagement.percent)}
                    </span>
                    <span className="font-heading text-xl font-semibold text-slate-900" data-testid="engagement-percent">
                      {engagement.percent}<span className="text-sm text-slate-500">%</span>
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex justify-between text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                  <span>Avoiding</span>
                  <span>Engaging</span>
                </div>
                <div className="mt-1"><ScoreBar percent={engagement.percent} /></div>
                <p className="mt-4 text-slate-700 text-sm leading-relaxed" data-testid="engagement-interpretation">
                  {engagement.interpretation}
                </p>
              </div>

              {skillVsEngagement && (
                <div className="mt-5 rounded-xl border border-primary/15 bg-accent p-5" data-testid="combined-interpretation">
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">
                    Skill × Engagement · {skillVsEngagement.label}
                  </p>
                  <p className="mt-2 text-slate-800 text-sm leading-relaxed">{skillVsEngagement.text}</p>
                </div>
              )}
            </section>
          )}

          {/* Strengths / Challenges / Recommendations */}
          <section className="p-8 sm:p-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="analysis-section">
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4 text-green-700">
                  <CheckCircle2 className="h-5 w-5" />
                  <h3 className="font-heading text-lg font-semibold">Strengths</h3>
                </div>
                <ul className="space-y-3 text-sm text-slate-700 leading-relaxed" data-testid="strengths-list">
                  {strengths.map((s, i) => <li key={i} className="flex gap-2"><span className="text-green-600">•</span><span>{s}</span></li>)}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="font-heading text-lg font-semibold">Challenges</h3>
                </div>
                <ul className="space-y-3 text-sm text-slate-700 leading-relaxed" data-testid="challenges-list">
                  {challenges.map((s, i) => <li key={i} className="flex gap-2"><span className="text-amber-600">•</span><span>{s}</span></li>)}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4 text-primary">
                  <Sparkles className="h-5 w-5" />
                  <h3 className="font-heading text-lg font-semibold">Recommendations</h3>
                </div>
                <ul className="space-y-3 text-sm text-slate-700 leading-relaxed" data-testid="recommendations-list">
                  {recommendations.map((s, i) => <li key={i} className="flex gap-2"><span className="text-primary">•</span><span>{s}</span></li>)}
                </ul>
              </CardContent>
            </Card>
          </section>

          <section className="px-8 sm:px-12 pt-2 pb-8 no-print" data-testid="feedback-cta-section">
            <div className="rounded-2xl border-2 border-dashed border-primary/40 bg-accent p-6 sm:p-8">
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-primary">
                This app is under active development
              </p>
              <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold text-slate-900">
                Please… PLEASE! We want your feedback.
              </h3>
              <p className="mt-2 text-slate-700 text-sm sm:text-base leading-relaxed">
                Help us improve. Was anything confusing, missing, or off? What would
                make this report more useful for you? Every note reaches the CGI team.
              </p>
              <div className="mt-5">
                <Button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event("cgi:open-feedback"))}
                  data-testid="feedback-cta-report-button"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                >
                  <MessageCircleHeart className="h-4 w-4" />
                  Share your feedback
                </Button>
              </div>
            </div>
          </section>

          <div className="px-8 sm:px-12 pb-10 text-xs text-slate-400">
            © Common Ground Institute · This report reflects self-perception and is intended for personal growth — not clinical diagnosis. This app is under active development — thank you for helping us improve.
          </div>
        </div>

        <div className="mt-10 flex justify-between flex-wrap gap-4 no-print">
          <Button asChild variant="outline">
            <Link to="/assessment" data-testid="retake-assessment-button">Retake the assessment</Link>
          </Button>
          <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white">
            <Link to="/" data-testid="back-to-dashboard-button">Back to dashboard</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
